import {TscThemeName} from '@techsmith/tsc-cloud-style-guide';

export enum ModalStates {
   closed,
   newReview,
   trialThenStartReview,
   trialOnly,
   deleteReview,
   deleteReviewWithComments,
   changeReviewByDate,
   buyNow,
   manageReviewers,
   manageReviewerGroups,
   selfIdentify,
   shareReview,
   shareReviewWithCreatedHeader,
   newVersion,
   editReviewTitle,
   editReviewDescription,
   leaveReview
}

export enum NotificationTypes {
   DeadlineApproaching = 'DeadlineApproaching',
   NewComment = 'NewComment',
   NewCommentReply = 'NewCommentReply',
   NewReviewConfirmation = 'NewReviewConfirmation',
   NewReviewInvitation = 'NewReviewInvitation',
   NewReviewVersionConfirmation = 'NewReviewVersionConfirmation',
   NewReviewVersionInvitation = 'NewReviewVersionInvitation',
   ReviewByDateModification = 'ReviewByDateModification',
   StartTrial = 'StartTrial',
   UserActivation = 'UserActivation'
}

export enum DeliveryTypes {
   Email = 'Email'
}

export enum DashboardViewLayout {
   grid = 'grid',
   list = 'list'
}

export enum MobileViewingModes {
   conversation,
   feedback
}

export enum AnnotationModes {
   edit,
   display,
   none
}

export enum HotjarTriggerNames {
   dashboardTrialUsersHasUsed = 'dashboard_trialUsers_hasUsed',
   dashboardAllUsersHitMaxReviews = 'dashboard_allUsers_hitMaxReviews',
   creationUploadAbandon = 'creation_upload_abandon',
   creationDescribeAbandon = 'creation_describe_abandon',
   creationInviteAbandon = 'creation_invite_abandon',
   modalStartTrialAbandon = 'modal_startTrial_abandon',
   modalBuyNowAbandon = 'modal_buyNow_abandon'
}

export enum PrivacyTypes {
   link = 'link',
   invite = 'invite'
}

export enum UserTypes {
   techSmith = 'TechSmith',
   selfIdentified = 'SelfIdentified'
}

export enum SortMetric {
   titleAscending = 'title_ascending',
   titleDescending = 'title_descending',
   createdDateAscending = 'createdDate_ascending',
   createdDateDescending = 'createdDate_descending'
}

export enum DashboardFilter {
   all = '',
   owned = 'owned',
   others = 'others'
}

export enum ButtonActivationKeys {
   enter = 13,
   space = 32
}

export enum ScreenOrientation {
   landscape = 'landscape',
   portrait = 'portrait'
}

export enum QuizState {
   questionOpen = 'questionOpen',
   setupOpen = 'setupOpen',
   closed = 'closed'
}

export default Object.freeze({
   hotjarTrigger: 'trigger',
   reviewerStatus: {
      added: 'added',
      invited: 'invited',
      joined: 'joined',
      pending: 'pending',
      removing: 'removing',
      failed: 'failed'
   },
   pluginType: {
      annotation: 'Annotation',
      markers: 'Markers'
   },
   reviewerRoles: {
      owner: 'Owner',
      reviewer: 'Reviewer',
      collaborator: 'Collaborator'
   },
   reviewerServerStatus: {
      failed: 'Failed'
   },
   operatingSysType: {
      mac: 'Mac',
      win: 'Win'
   },
   annotationColors: {
      white: '#fff',
      blue: '#00aeff',
      green: '#36be5f',
      red: '#ff6666',
      yellow: '#ffc92f'
   },
   colorClass: {
      white: 'white',
      blue: 'blue',
      green: 'green',
      red: 'red',
      yellow: 'yellow'
   },
   keys: {
      backspace: 8,
      enter: 13,
      escape: 27,
      delete: 46,
      space: 32,
      shift: 16
   },
   cursors: {
      default: 'default'
   },
   errorReasons: {
      removedReviewer: 'removedReviewer'
   },
   statusCodes: {
      ok: 200,
      created: 201,
      accepted: 202,
      noContent: 204,
      unauthorized: 401,
      forbidden: 403,
      notFound: 404,
      conflict: 409,
      serverError: 500
   },
   user: {
      failedName: '(User Lookup Failed)',
      deletedName: 'Deleted User',
      defaultAvatar: null,
      identifyDefaultAvatarFileName: 'profile-avatar-default-image.png',
      deletedUserAvatarBackgroundColor: '#9EA8AE',
      deletedUserAvatarDisplayName: 'x'
   },
   comment: {
      maxLength: 4000
   },
   groupName: {
      maxLength: 128
   },
   email: {
      maxLength: 129
   },
   title: {
      maxLength: 100
   },
   prompt: {
      maxLength: 500
   },
   selfIdentifyName: {
      maxLength: 64
   },
   annotation: {
      timeTolerance: 0.1,
      animationDuration: 200,
      preloadAnnotationScale: 0.9,
      scale: 1000,
      type: {
         rect: 'rect',
         freehand: 'path',
         arrow: 'arrow'
      },
      controlsPadding: 10,
      strokeWidth: 5,
      colors: ['#FFF', '#00AEFF', '#36BE5F', '#FF6666', '#FFC92F'],
      defaultColor: '#FFC92F',
      transparentColor: 'transparent'
   },
   textAreaMaxRows: {
      feedback: 3
   },
   reactionTypes: {
      like: 'Like'
   },
   imageLayout: {
      maxFileBytesToRead: 65635
   },
   refreshIntervalInMs: 60000,
   themeName: {
      default: TscThemeName.dawn
   },
   localStorageKeys: {
      editComment: 'editComment',
      addReply: 'addReply',
      addComment: 'addComment',
      dashboardLayout: 'DashboardLayout',
      themeName: 'InterfaceTheme',
      hotjar: 'Hotjar',
      selfIdentifiedId: 'SelfIdentifiedId',
      selfIdentifiedSecret: 'SelfIdentifiedSecret',
      selfIdentifiedName: 'SelfIdentifiedName',
      reviewsSortMetric: 'ReviewsSortMetric',
      camtasiaCookiesAcknowledged: 'CamtasiaCookiesAcknowledged'
   },
   entitlements: {
      feedbackProduct: 'Feedback',
      status: {
         active: 'Active',
         grace: 'Grace',
         canceled: 'Canceled'
      },
      levels: {
         trial: 'Trial',
         full: 'Basic'
      },
      permissions: {
         createReview: 'review.create',
         startTrial: 'trial.create'
      },
      authorizations: {
         canCreateReviews: 'canCreateReviews',
         canStartTrial: 'canStartTrial'
      },
      warningDaysLimit: 5,
      cautionDaysLimit: 10
   },
   elementTypes: {
      textArea: 'textarea',
      input: 'input',
      search: 'search'
   },
   siteLinks: {
      help: {
         center: 'https://www.techsmith.com/redirect.asp?target=helpcenter&ver=1.*.*&lang=enu&os=na&product=videoreview',
         access: 'https://www.techsmith.com/redirect.asp?target=help_reviewaccess&ver=1.*.*&lang=enu&os=na&product=videoreview',
         reviewers: 'https://www.techsmith.com/redirect.asp?target=help_managereviewers&ver=1.*.*&lang=enu&os=na&product=videoreview'
      },
      activity: '/activity',
      support: 'https://support.techsmith.com/hc/en-us',
      tos: '/termsofservice',
      signin: '/signin',
      signout: '/signout',
      techsmith: 'https://www.techsmith.com',
      dashboard: '/',
      preferences: '/preferences',
      review: '/review',
      experimentalFeaturesSurvey: 'https://insights.hotjar.com/s?siteId=507927&surveyId=37298',
      videoReviewSplash: 'https://www.techsmith.com/video-review'
   },
   conversationsSiteLinks: {
      tos: '/TechSmithRelayGeneralTermsofUse.pdf',
      signin: '/Present/RedirectAfterSignin?url={param0}'
   },
   assetLinks: {
      onboardingImage: '/assets/Onboarding.gif',
      lockImage: 'data:image/svg+xml,%0A%3Csvg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="172.5" height="242.88" viewBox="0 0 172.5 242.88"%3E%3Cdefs%3E%3ClinearGradient id="b7cda584-116a-4acb-869b-35b647a0dd69" data-name="New Gradient Swatch 5" x1="15.04" y1="55.47" x2="158.49" y2="198.92" gradientUnits="userSpaceOnUse"%3E%3Cstop offset="0" stop-color="%2335abab"/%3E%3Cstop offset="0.67" stop-color="%23676e96"/%3E%3Cstop offset="0.89" stop-color="%235c568f"/%3E%3Cstop offset="1" stop-color="%2356488b"/%3E%3C/linearGradient%3E%3C/defs%3E%3Ctitle%3Egraphic_state_lock%3C/title%3E%3Cg style="isolation: isolate"%3E%3Cg id="fe5df712-2788-47ae-a05d-d7ef571aa0be" data-name="Layer 1"%3E%3Cg%3E%3Cg%3E%3Cpath d="M147.75,96.23V62a62,62,0,0,0-62-62h0a62,62,0,0,0-62,62V97.28a86.24,86.24,0,1,0,124-1ZM85.75,16a46.06,46.06,0,0,1,46,46V83.4a86,86,0,0,0-92,.64V62A46.06,46.06,0,0,1,85.75,16Z" style="fill: url(%23b7cda584-116a-4acb-869b-35b647a0dd69)"/%3E%3Cg%3E%3Cpath d="M86.25,90.62a66,66,0,1,0,66,66A66.08,66.08,0,0,0,86.25,90.62Zm54.44,95.7-2.81-1.62a2,2,0,0,0-2,3.47l2.77,1.6a62.3,62.3,0,0,1-20.11,19.78l-1.62-2.79a2,2,0,0,0-2.73-.74,2,2,0,0,0-.73,2.74l1.6,2.77a61.73,61.73,0,0,1-27.31,7.06v-3.2a2,2,0,0,0-4,0v3.18a61.86,61.86,0,0,1-27.2-7.51l1.62-2.8a2,2,0,1,0-3.46-2L53.11,209a62.16,62.16,0,0,1-19.79-20.12l2.8-1.61a2,2,0,0,0-2-3.48l-2.78,1.62a61.72,61.72,0,0,1-7.07-27.33H27.5a2,2,0,0,0,0-4H24.3a61.68,61.68,0,0,1,7.51-27.18l2.81,1.62a2.14,2.14,0,0,0,1,.27,2,2,0,0,0,1-3.75l-2.78-1.6A62.4,62.4,0,0,1,54,103.69l1.63,2.8a2,2,0,0,0,1.73,1,1.93,1.93,0,0,0,1-.27,2,2,0,0,0,.73-2.73l-1.61-2.79a61.49,61.49,0,0,1,27.32-7v3.24a2,2,0,0,0,4,0V94.67a61.6,61.6,0,0,1,27.2,7.51L114.33,105a2,2,0,0,0,.73,2.73,1.91,1.91,0,0,0,1,.27,2,2,0,0,0,1.73-1l1.6-2.78a62.46,62.46,0,0,1,19.8,20.11L136.38,126a2,2,0,0,0,1,3.73,2.13,2.13,0,0,0,1-.26l2.78-1.61a61.56,61.56,0,0,1,7.07,27.3H145a2,2,0,0,0,0,4h3.2A61.73,61.73,0,0,1,140.69,186.32Z" style="fill: %23193138;opacity: 0.3"/%3E%3Cg style="opacity: 0.1;mix-blend-mode: multiply"%3E%3Cpath d="M145,159.11h3.2a61.73,61.73,0,0,1-7.51,27.21l-2.81-1.62a2,2,0,0,0-2,3.47l2.77,1.6a62.3,62.3,0,0,1-20.11,19.78l-1.62-2.79a2,2,0,0,0-2.73-.74,2,2,0,0,0-.73,2.74l1.6,2.77a61.73,61.73,0,0,1-27.31,7.06v-3.2a2,2,0,0,0-4,0v3.18a61.86,61.86,0,0,1-27.2-7.51l1.62-2.8a2,2,0,1,0-3.46-2L53.11,209a62.16,62.16,0,0,1-19.79-20.12l2.8-1.61a2,2,0,0,0-2-3.48l-2.78,1.62a61.72,61.72,0,0,1-7.07-27.33H27.5a2,2,0,0,0,0-4H24.3a61.68,61.68,0,0,1,7.51-27.18l2.81,1.62a2.14,2.14,0,0,0,1,.27,2,2,0,0,0,1-3.75l-2.78-1.6A62.4,62.4,0,0,1,54,103.69l1.63,2.8a2,2,0,0,0,1.73,1,1.93,1.93,0,0,0,1-.27,2,2,0,0,0,.73-2.73l-1.61-2.79a61.49,61.49,0,0,1,27.32-7v3.24a2,2,0,0,0,4,0V94.67a61.6,61.6,0,0,1,27.2,7.51L114.33,105a2,2,0,0,0,.73,2.73,1.91,1.91,0,0,0,1,.27,2,2,0,0,0,1.73-1l1.6-2.78a62.46,62.46,0,0,1,19.8,20.11L136.38,126a2,2,0,0,0,1,3.73,2.13,2.13,0,0,0,1-.26l2.78-1.61a61.56,61.56,0,0,1,7.07,27.3H145a2,2,0,0,0,0,4Z" style="fill: %23193138"/%3E%3C/g%3E%3C/g%3E%3Cpath d="M85.25,131.62a24,24,0,1,0,24,24A24,24,0,0,0,85.25,131.62Zm10.2,27L81.9,167.55a3,3,0,0,1-4.65-2.5V147.2a3,3,0,0,1,4.65-2.5l13.55,8.92A3,3,0,0,1,95.45,158.62Z" style="fill: %23193138;opacity: 0.3"/%3E%3C/g%3E%3Cpath d="M147.75,96.23V62a62,62,0,0,0-62-62h0a62,62,0,0,0-62,62V97.28L39.75,84V62a46,46,0,0,1,92,0V83.4Z" style="fill: %23193138;opacity: 0.2"/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A'
   },
   splashStartTrialQueryParamString: 'startTrial',
   filterQueryParamString: 'filter',
   openBuyNowModalQueryParamString: 'buyNow',
   googleAnalytics: {
      propertyId: 'UA-2104241-75',
      preferenceChange: {
         category: 'preference-change',
         actions: {
            commentNotification: 'comment-notification',
            replyNotification: 'reply-notification'
         },
         labels: {
            disabled: 'disabled',
            enabled: 'enabled'
         }
      },
      reviewByDate: {
         category: 'review-by-date',
         actions: {
            changed: 'changed',
            dontSend: 'dont-send-notifications',
            send: 'send-notifications'
         }
      },
      subscription: {
         category: 'subscription',
         action: 'purchase'
      },
      trial: {
         category: 'trial',
         action: 'start'
      },
      exportComments: {
         category: 'export-comments',
         actions: {
            exportFromPopUpMenu: 'export-from-popup',
            exportFromDeleteModal: 'export-from-delete-modal'
         }
      },
      deleteReview: {
         category: 'delete-review',
         actions: {
            reviewHasComments: 'review-has-comments',
            reviewHasNoComments: 'review-has-no-comments'
         }
      },
      guestUserLogIn: {
         category: 'guest-user-login-with-techsmith-account',
         action: 'logged-in-with-techsmith-account'
      },
      createReview: {
         category: 'create-review',
         actions: {
            startCreateProcess: 'start-review-clicked',
            uploadMedia: 'media-uploaded',
            reviewCreated: 'review-created'
         }
      },
      commenting: {
         category: 'commenting',
         actions: {
            createComment: 'comment-created',
            createEmptyComment: 'empty-comment-created',
            createReply: 'reply-created',
            likeComment: 'comment-liked',
            editComment: 'comment-edited',
            deleteCommentText: 'comment-text-deleted',
            deleteComment: 'comment-deleted',
            resolveComment: 'comment-resolved'
         },
         labels: {
            reviewOwner: 'review-owner',
            nonReviewOwner: 'non-review-owner'
         }
      },
      editReview: {
         category: 'edit-review',
         action: 'edit-review',
         labels: {
            editTitle: 'edit-title',
            editDescription: 'edit-description',
            editTitleAndDescription: 'edit-title-and-description'
         }
      },
      versioning: {
         category: 'versioning',
         actions: {
            versionCreated: 'version-{param0}-created',
            VersionMediaUploaded: 'version-media-uploaded'
         },
         labels: {
            video: 'video',
            image: 'image'
         }
      },
      shareReview: {
         category: 'share-review',
         actions: {
            openShareReviewModal: 'open-share-review-modal',
            openManageReviewModal: 'open-manage-reviewers-modal',
            editReviewers: 'reviewers-edited',
            changePrivacyType: 'review-privacy-type-change'
         },
         labels: {
            switchToInvite: 'switch-to-invite',
            switchToLink: 'switch-to-link'
         }
      },
      leaveReview: {
         category: 'leave-review',
         action: 'leave-review'
      },
      loadMoreReviews: {
         category: 'load-more-reviews',
         action: 'load-more-reviews'
      },
      sortReviews: {
         category: 'sort-reviews',
         action: {
            titleAsc: 'sorted-by-title-A-to-Z',
            titleDesc: 'sorted-by-title-Z-to-A',
            dateAsc: 'sorted-by-oldest-first',
            dateDesc: 'sorted-by-newest-first'
         }
      }
   },
   dashboardEmptyStateMaxWidth: '20rem',
   preventScrollClassName: 'prevent-scroll',
   commentContainerClassName: 'comments',
   mobileCommentContainerClassName: 'conversation',
   mediaTimePollingInterval: 200,
   mobileWidthBreakpoint: 920,
   mobileOrientationBufferZone: 200,
   invalidVersionNumberError: 'invalid-version-error',
   initialTakeValue: 100
});
